import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface Vitola {
  id: string
  name: string
  description?: string
  length?: number
  ringGauge?: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface VitolaResponse {
  success: boolean
  data: { vitolas: Vitola[] }
  message: string
}

export interface SingleVitolaResponse {
  success: boolean
  data: { vitola: Vitola }
  message: string
}

export const vitolaApi = createApi({
  reducerPath: 'vitolaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/vitolas',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Vitola'],
  endpoints: (builder) => ({
    getVitolas: builder.query<Vitola[], { active?: boolean; cigarLineId?: string } | void>({
      query: (params = {}) => {
        const queryParams: Record<string, string | boolean> = {}
        if (params && params.active !== undefined) {
          queryParams.active = params.active
        }
        if (params && params.cigarLineId) {
          queryParams.cigarLineId = params.cigarLineId
        }
        return {
          url: '/',
          params: queryParams,
        }
      },
      transformResponse: (response: VitolaResponse) => response.data.vitolas,
      providesTags: ['Vitola'],
    }),
    getVitolaById: builder.query<Vitola, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: SingleVitolaResponse) => response.data.vitola,
      providesTags: (_, __, id) => [{ type: 'Vitola', id }]
    }),
    createVitola: builder.mutation<Vitola, Partial<Vitola>>({
      query: (vitola) => ({
        url: '/',
        method: 'POST',
        body: vitola
      }),
      transformResponse: (response: SingleVitolaResponse) => response.data.vitola,
      invalidatesTags: ['Vitola']
    }),
    updateVitola: builder.mutation<Vitola, { id: string; updates: Partial<Vitola> }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updates
      }),
      transformResponse: (response: SingleVitolaResponse) => response.data.vitola,
      invalidatesTags: (_, __, { id }) => [{ type: 'Vitola', id }, 'Vitola']
    }),
    deleteVitola: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Vitola']
    })
  })
})

export const {
  useGetVitolasQuery,
  useGetVitolaByIdQuery,
  useCreateVitolaMutation,
  useUpdateVitolaMutation,
  useDeleteVitolaMutation
} = vitolaApi
